import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { isFileDownloadObject } from '../../utils/TypeUtils';
import { FileDownloadLink } from './FileDownloadLink';

export type FieldValue =
  | string
  | number
  | boolean
  | File
  | { fileId: string; filename: string; isFile: true }
  | null
  | undefined
  | Array<any>;

interface ReviewFieldValueProps {
  value: FieldValue;
  fieldId?: string;
  className?: string;
}

interface FileDisplayProps {
  file: File | string;
}

const FileDisplay: React.FC<FileDisplayProps> = React.memo(({ file }) => {
  const { isPDF, fileUrl, fileName } = useMemo(() => {
    const isFileObject = file instanceof File;
    const url = isFileObject ? URL.createObjectURL(file) : file;

    const pdfCheck = isFileObject
      ? file.type === 'application/pdf'
      : typeof file === 'string' && file.toLowerCase().endsWith('.pdf');

    const name =
      isFileObject && file.name
        ? file.name
        : typeof file === 'string'
        ? file.split('/').pop() || 'document'
        : 'document';

    return {
      isPDF: pdfCheck,
      fileUrl: url,
      fileName: name,
    };
  }, [file]);

  if (isPDF) {
    return (
      <a
        href={fileUrl}
        target='_blank'
        rel='noopener noreferrer'
        className='inline-flex items-center gap-2 text-reskin-primary-blue hover:underline'
      >
        <span>{fileName}</span>
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='w-4 h-4' />
      </a>
    );
  }

  return (
    <div className='border border-dashed p-2'>
      <img
        className='object-cover w-1/2 h-auto mx-auto'
        src={fileUrl}
        alt='uploaded file'
        loading='lazy'
      />
    </div>
  );
});

FileDisplay.displayName = 'FileDisplay';

const ArrayDisplay: React.FC<{ items: Array<any>; fieldId?: string }> =
  React.memo(({ items, fieldId }) => {
    return (
      <div className='space-y-2'>
        {items.map((item, index) => {
          const key =
            item !== null && typeof item === 'object' && 'id' in item
              ? item.id
              : `${fieldId || 'field'}-${index}`;

          return (
            <div key={key} className='break-all sm:break-normal'>
              {String(item)}
            </div>
          );
        })}
      </div>
    );
  });

ArrayDisplay.displayName = 'ArrayDisplay';

export const ReviewFieldValue: React.FC<ReviewFieldValueProps> = React.memo(
  ({ value, fieldId, className = '' }) => {
    if (value === null || value === undefined || value === '') {
      return <span className={className}>-</span>;
    }

    // Handle file objects from getQuestionAnswers
    if (isFileDownloadObject(value)) {
      return (
        <FileDownloadLink fileId={value.fileId} filename={value.filename} />
      );
    }

    if (Array.isArray(value)) {
      return <ArrayDisplay items={value} fieldId={fieldId} />;
    }

    if (
      value instanceof File ||
      (typeof value === 'string' && value.startsWith('http'))
    ) {
      return <FileDisplay file={value} />;
    }

    if (typeof value === 'boolean') {
      return <p className={className}>{value ? 'Yes' : 'No'}</p>;
    }

    return (
      <p className={`whitespace-pre-line ${className}`}>{String(value)}</p>
    );
  },
);

ReviewFieldValue.displayName = 'ReviewFieldValue';
